import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, map, switchMap, of } from 'rxjs';
import { ApiService } from './api.service';
import { AccountService } from './account.service';
import { GetImage } from '../helpers/getImage';
import { JwtHelperService } from '@auth0/angular-jwt';
import { calculateDays } from '../helpers';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  userData: any;
  img = null;
  user: any;

  constructor(
    private readonly accountService: AccountService,
    private _api: ApiService,
    private getImage: GetImage,
    private sanitizer: DomSanitizer,
    private jwtHelper: JwtHelperService,
  ) {}

  getUserDetails(reload = true) {
    let userId = this.jwtHelper.decodeToken(localStorage.getItem('access_token')) ?? '';
    userId = userId['termhub:id'];
    let user: any = localStorage.getItem('user_data');
    user = JSON.parse(user);
    this.userData = {
      email: user?.username,
      userId: user?.id,
      name: user?.name ? user.name : (user?.uriLabel ?? ''),
      uriLabel: user?.uriLabel,
    };

    if (reload) {
      this.getUserInfo(this.userData.userId ?? userId);
    }
    this.getUserIcon(this.userData.userId ?? userId);

    return this.userData;
  }

  getUserInfo(userId: string) {
    this.accountService.getUserDetails(userId).subscribe((data) => {
      this.user = data;
    });
  }

  getUserClaim() {
    return this.jwtHelper.decodeToken(localStorage.getItem('access_token'));
  }

  processUserExpiration() {
    const userClaim = this.getUserClaim();
    const days = calculateDays(this.user?.planExpiration) ?? 0;

    if (days <= 0 && userClaim['termhub:plan'] === 'GRACE_PERIOD') {
      return 'GRACEPERIOD';
    } else if (days <= 0 && userClaim['termhub:plan'] === 'EXPIRED') {
      return 'EXPIRED';
    } else if (days <= 0 && userClaim['termhub:plan'] === 'EXPIRED-WITH-ORG') {
      return 'EXPIREDWITHORG';
    } else if (days <= 30) {
      return 'WARNING';
    } else return '';
  }

  getUserClaimExpiration() {
    const userClaim = this.getUserClaim();
    try {
      if (userClaim['termhub:plan'] === 'GRACE_PERIOD') {
        return 'GRACEPERIOD';
      } else if (userClaim['termhub:plan'] === 'EXPIRED') {
        return 'EXPIRED';
      } else return '';
    } catch (e) {
      return '';
    }
  }

  getUserIcon(id: string) {
    if (!id) return;
    this.accountService.downloadUserIconImage(id).subscribe((data) => {
      if (data) {
        this.img = this.createImageFromBlob(data);
      } else {
        this.img = null;
      }
    });
  }

  createImageFromBlob(image: Blob) {
    if (image) {
      const unsafeImageUrl = URL?.createObjectURL(image);
      return this.sanitizer?.bypassSecurityTrustUrl(unsafeImageUrl);
    }
    return null;
  }

  flush() {
    this.userData = {};
    this.img = null;
  }

  getAllUsers(query?: any, offset?: any, limit?: any, sort?: any, ascending = true, handler = ''): Observable<any> {
    let isQuery = '';
    const eq = query ? encodeURIComponent(query) : '';
    if (offset || limit || query || sort || ascending) {
      isQuery = `?query=${eq}&offset=${offset || 0}&limit=${limit || 10}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}${handler ? `&handler=${handler}` : ''}`;
    } else {
      isQuery = '';
    }
    return this._api.get(`/user${isQuery}`).pipe(
      map((users: any) => {
        users?.items?.map((user) => {
          user.nick =
            user.name?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase();
          this.getImage.user(user.id).subscribe(
            (imageUrl) => {
              user.image = imageUrl;
            },
            (error) => {
              console.log(error);
              user.image = null;
            },
          );
        });
        return users;
      }),
    );
  }

  // ACCEPTS user role ADMIN or USER only
  updateUserData(id: string, data: any): Observable<any> {
    return this._api.patch(`/user/${id}`, data).pipe(
      map((user: any) => {
        user.nick =
          user.name?.substring(0, 2).toUpperCase() || user.uriLabel?.substring(0, 2).toUpperCase() || user.username?.substring(0, 2).toUpperCase();
        this.getImage.user(user.id).subscribe(
          (imageUrl) => {
            user.image = imageUrl;
          },
          (error) => {
            console.log(error);
            user.image = null;
          },
        );
      }),
    );
  }

  /**
   * @param Offset - offset is 0 by default
   * @param limit - limit is 10 by default
   */
  getUsersFor(
    usersFor: 'project' | 'organization' | 'team',
    id: string,
    query?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    ascending = true,
    handler = '',
  ): Observable<any> {
    let isQuery = '';
    const eq = query ? encodeURIComponent(query) : '';
    if (offset || limit || query || sort || ascending) {
      isQuery = `?query=${eq}&offset=${offset || 0}&limit=${limit || 10}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}${handler ? `&handler=${handler}` : ''}`;
    } else {
      isQuery = '';
    }
    return this._api.get(`/${usersFor}/${id}/users${isQuery}`).pipe(
      map((users: any) => {
        users?.items?.map((user) => {
          user.nick = user?.name ? user.name?.substring(0, 2).toUpperCase() : user.uriLabel?.substring(0, 2).toUpperCase();
          this.getImage.user(user.id).subscribe(
            (imageUrl) => {
              user.image = imageUrl;
            },
            (error) => {
              user.image = null;
            },
          );
        });
        return users;
      }),
    );
  }

  // REMOVE USER FROM PROJECT
  removeUserFromProject(projectId: string, userId: string, userRole: string) {
    return this._api.delete(`/project/${projectId}/${userRole}/user/${userId}`).pipe(map((data) => data));
  }

  // ADD USER TO PROJECT
  addUserToProject(projectId: string, userId: string, userRole: string) {
    return this._api.post(`/project/${projectId}/${userRole}/user`, userId).pipe(map((data) => data));
  }

  // Verify User by email
  verifyUser(email: string): Observable<any> {
    return this._api.get(`/user?query=username:${encodeURIComponent(email)}&offset=0&limit=1`, null).pipe(
      switchMap((data: any) => {
        if (data.total > 0) {
          return this._api.post(`/user/check?username=${encodeURIComponent(email)}&verified=true`, null).pipe(
            map((user: any) => {
              if (user.verified) {
                return { valid: true, id: data.items[0].id };
              } else {
                return { valid: false };
              }
            }),
          );
        } else {
          return of({ valid: false });
        }
      }),
    );
  }

  // DELETE USER
  deleteUser(userId, cascade = false): Observable<any> {
    return this._api.delete(`/user/${userId}${cascade ? '?cascade=true' : ''}`).pipe(map((data) => data));
  }

  // ASSUME USER
  assumeUser(userId: string): Observable<any> {
    return this._api.post(`/auth/proxy?expiresIn=14400`, { 'termhub:id': userId }).pipe(map((data) => data));
  }

  ChangeProjectUserRole(projectId: string, currentRole: string, userId: string, newRole: string): Observable<any> {
    return this._api.put(`/project/${projectId}/${currentRole}/user/${userId}`, newRole).pipe(map((data) => data));
  }
  KpiMetrics(role: string, searchQuery?: string, field?: string): Observable<any> {
    const url = this.constructUrl(role, searchQuery, field);
    return this._api.get(url).pipe(map((data) => data));
  }

  private constructUrl(role: string, searchQuery?: string, field?: string): string {
    if (!field) {
      return searchQuery ? `/${role}?query=${encodeURIComponent(searchQuery)}&offset=0&limit=10000` : `/${role}?offset=0&limit=10000`;
    } else {
      return `/${role}?field=${field}&query=${encodeURIComponent(searchQuery)}&offset=0&limit=10000`;
    }
  }

  getUsers(query?: any, offset?: any, limit?: any, sort?: any, ascending = true): Observable<any> {
    let isQuery = '';
    const eq = query ? encodeURIComponent(query) : '';
    if (offset || limit || query || sort || ascending) {
      isQuery = `?query=${eq}&offset=${offset || 0}&limit=${limit || 10}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}`;
    } else {
      isQuery = '';
    }
    return this._api.get(`/user${isQuery}`).pipe(map((data) => data));
  }
}
